import { graphql } from '@/gql';
import { usePermissions } from '@/hooks/usePermissions';
import SiteLocationEditorModal from '@/ui/components/siteLocation/SiteLocationEditorModal';
import SiteLocationThumbnail from '@/ui/components/siteLocation/SiteLocationThumbnail';
import { Card, ContextMenuDropdown, H4, Text } from '@payaca/ambrosia';
import Address2Lines from '@payaca/components/address/Address2Lines';
import { AddressPermissions } from '@payaca/permissions/addresses/addresses.permissions';
import {
  getAddressCoordinates,
  getGoogleMapsDirectionsUrl,
  getGoogleMapsSearchUrl,
  getMapDestination,
} from '@payaca/helpers/locationHelper';
import type { ResultOf } from '@graphql-typed-document-node/core';
import { FC, useState } from 'react';

export const SiteAddressCard_Address = graphql(`
  fragment SiteAddressCard_Address on Address {
    id
    line1
    line2
    city
    administrativeArea
    postalCode
    country
    fullLocalAddress
    coordinates {
      latitude
      longitude
    }
    lookupCoordinates {
      latitude
      longitude
    }
    manualCoordinates {
      latitude
      longitude
    }
  }
`);

export type SiteAddressCardProps = {
  address: ResultOf<typeof SiteAddressCard_Address>;
  /** Site addresses beyond the one shown, which the card only counts. */
  furtherAddressCount: number;
  projectId: string;
  onEditAddress: () => void;
};

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const SiteAddressCard: FC<SiteAddressCardProps> = (props) => {
  const { address, furtherAddressCount, projectId, onEditAddress } = props;
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const { userHasRequiredPermission } = usePermissions();
  // There is nothing to drag without a pin, and the editor would otherwise open
  // on its fallback centre, which reads as the site.
  const canAdjustPin =
    userHasRequiredPermission([AddressPermissions.UPDATE_ADDRESS]) &&
    getAddressCoordinates(address.coordinates) !== null;

  const destination = getMapDestination(
    address.coordinates,
    address.fullLocalAddress
  );

  const menuItems = [
    ...(canAdjustPin
      ? [
          {
            icon: 'marker-pin-01.3' as const,
            label: 'Adjust pin',
            onClick: () => {
              setIsEditorOpen(true);
            },
          },
        ]
      : []),
    ...(destination
      ? [
          {
            icon: 'marker-pin-01.3' as const,
            label: (
              <>
                Open in maps
                <span className="sr-only"> (opens in a new tab)</span>
              </>
            ),
            onClick: () => openInNewTab(getGoogleMapsSearchUrl(destination)),
          },
          {
            icon: 'link-external-01.3' as const,
            label: (
              <>
                Directions
                <span className="sr-only"> (opens in a new tab)</span>
              </>
            ),
            onClick: () =>
              openInNewTab(getGoogleMapsDirectionsUrl(destination)),
          },
        ]
      : []),
    {
      icon: 'edit-01.3' as const,
      label: 'Edit address',
      onClick: onEditAddress,
    },
  ];

  return (
    <>
      <Card>
        <Card.Body className="flex h-full flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <H4>Site address</H4>
            <ContextMenuDropdown
              label="Site address actions"
              variant="quaternary"
              size="xs"
              menuItems={menuItems}
            />
          </div>

          <Address2Lines address={address} />

          <SiteLocationThumbnail
            coordinates={address.coordinates}
            addressText={address.fullLocalAddress}
            onActivate={() => setIsEditorOpen(true)}
          />

          {furtherAddressCount > 0 && (
            <Text level="supporting">+{furtherAddressCount} more</Text>
          )}
        </Card.Body>
      </Card>
      <SiteLocationEditorModal
        address={address}
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        canEdit={canAdjustPin}
        projectId={projectId}
      />
    </>
  );
};

export default SiteAddressCard;
